// Patient-workspace loader (Blueprint §2 screen 5). Ports legacy
// openPatientWorkspace's DATA half (v20 lines ~2528-2584): the case read plus
// the eight parallel per-patient queries, each loaded INDEPENDENTLY so a single
// failed panel shows an inline retry — never a red whole-screen (R-003 per-query
// failure taxonomy). The 8th query is the ownership history (the round-table
// feature): audit_events case_assigned, newest 50, which merges into the
// timeline and degrades SILENTLY (its failure never reddens the workspace).
//
// LATEST-WINS: the caller passes a flight().begin() isStale probe; the loader
// checks it after every await and abandons a stale load without committing (a
// nurse who clicks patient B while A is still loading must never see A's data
// land under B's header — legacy wsEpoch).
//
// The owner NAME is resolved from the loaded staff directory (userName), not a
// PostgREST embedded join, so it needs no extra column and works uniformly.

import { getClient, ok, fail } from '../db/client.js';
import { TABLES } from '../db/contract.js';
import { store, SLICES } from '../state/store.js';
import { logAudit, AUDIT_EVENT } from './audit.js';

/**
 * The eight per-patient panel queries, by key, in legacy order. Each is
 * independent — its own {data,error}. `assignHist` (audit) is the 8th and is
 * treated as non-critical everywhere downstream.
 * @param {any} client
 * @param {string} caseId
 * @returns {Record<string, any>} builders keyed by panel
 */
function panelQueries(client, caseId) {
  return {
    tasks: client.from(TABLES.TASKS).select('*').eq('case_id', caseId),
    contacts: client.from(TABLES.CONTACTS).select('*').eq('case_id', caseId),
    aes: client.from(TABLES.ADVERSE_EVENTS).select('*').eq('case_id', caseId),
    deliveries: client.from(TABLES.DELIVERIES).select('*').eq('case_id', caseId),
    cycles: client.from(TABLES.MEDICATION_CYCLES).select('*').eq('case_id', caseId).order('cycle_no', { ascending: false }),
    stageHist: client.from(TABLES.CASE_STAGE_HISTORY).select('*').eq('case_id', caseId).order('created_at', { ascending: false }),
    docs: client.from(TABLES.DOCUMENTS).select('*').eq('case_id', caseId).eq('is_active', true).order('created_at', { ascending: false }),
    // Ownership history (Q4): per-case, tiny, non-critical. occurred_at is the
    // audit_events time column (same as the audit trail). limit 50.
    assignHist: client
      .from(TABLES.AUDIT_EVENTS)
      .select('*')
      .eq('case_id', caseId)
      .eq('event_type', 'case_assigned')
      .order('occurred_at', { ascending: false })
      .limit(50),
  };
}

/**
 * How many of the CLINICAL panels failed (the 7 excl. the silent audit panel) —
 * drives the "Some patient data didn't load (n/7)" honest partial-load toast.
 * @param {Record<string, {error?: any}>} panels
 * @returns {number}
 */
export function clinicalFailCount(panels) {
  const keys = ['tasks', 'contacts', 'aes', 'deliveries', 'cycles', 'stageHist', 'docs'];
  return keys.filter((k) => panels && panels[k] && panels[k].error).length;
}

/**
 * Load a patient's workspace and publish SLICES.WORKSPACE. Never throws.
 *
 * Slice shape: { caseId, case, panels{…}, loadError, notFound }
 *  - loadError set (case read failed) → the header shows an in-place retry;
 *  - notFound true (clean read, no row) → "not a case" state (consent fallback);
 *  - each panels[key] = {data, error} — a failed panel renders its own retry.
 *
 * @param {string} caseId
 * @param {{client?: any, isStale?: () => boolean}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadWorkspace(caseId, deps) {
  const client = (deps && deps.client) || getClient();
  const isStale = (deps && deps.isStale) || (() => false);

  // 1. The case itself (fatal if it errors — but a NETWORK error ≠ missing row).
  const { data: c, error: cErr } = await client.from(TABLES.CASES).select('*').eq('case_id', caseId).maybeSingle();
  if (isStale()) return fail(null, 'STALE'); // a newer patient took over — abandon
  if (cErr) {
    store.set(SLICES.WORKSPACE, { caseId, case: null, panels: {}, loadError: cErr, notFound: false });
    return fail(cErr);
  }
  if (!c) {
    // Clean read, no case. It may be a consent submission not yet promoted — the
    // intake overlay (Phase 3) owns that; here we surface it honestly, not as an
    // error and not as a blank patient.
    store.set(SLICES.WORKSPACE, { caseId, case: null, panels: {}, loadError: null, notFound: true });
    return ok({ notFound: true });
  }

  // 2. The eight panels, in parallel — each keeps its own error.
  const q = panelQueries(client, caseId);
  // Register-migration history: query legacy_episodes ONLY for cases that
  // carry legacy provenance, so a workspace opened before the operator has
  // run the legacy-load SQL pack (table absent) never throws a panel error
  // at the nurse for ordinary patients.
  if (c.legacy_source) {
    q.episodes = client
      .from(TABLES.LEGACY_EPISODES)
      .select('*')
      .eq('case_id', caseId)
      .order('month_key', { ascending: false })
      .limit(500);
  }
  const keys = Object.keys(q);
  const settled = await Promise.all(keys.map((k) => Promise.resolve(q[k]).then((r) => r).catch((e) => ({ data: null, error: e }))));
  if (isStale()) return fail(null, 'STALE'); // stale mid-load — don't commit

  /** @type {Record<string, {data: any[], error: any}>} */
  const panels = {};
  keys.forEach((k, i) => {
    const r = settled[i] || {};
    panels[k] = { data: r.data || [], error: r.error || null };
  });

  store.set(SLICES.WORKSPACE, { caseId, case: c, panels, loadError: null, notFound: false });
  return ok({ caseId, case: c, panels });
}

/**
 * Log a patient contact (legacy openCallLog → contacts insert). The HONEST call
 * model (Blueprint §2 Workspace): the app never claims a call happened or was
 * recorded — the nurse records the OUTCOME of a call they placed themselves.
 * Writes one contacts row, then audits it. Returns the envelope.
 * @param {string} caseId
 * @param {{channel?: string, outcome: string, note?: string}} entry
 * @param {{client?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
/**
 * Build the cases patch for a clinical-details save, dropping any field the form
 * left untouched. Pure and exported so the "blank means leave it alone" rule is
 * testable without a database: a nurse who opens the panel to set a stage must
 * not silently wipe a diagnosis somebody else recorded.
 * @param {{diagnosis?: string, cancer_stage?: string, delivery_method?: string}|null|undefined} form
 * @returns {Record<string, any>} patch — empty when nothing was entered
 */
export function clinicalPatch(form) {
  const f = form || {};
  /** @type {Record<string, any>} */
  const patch = {};
  const dx = String(f.diagnosis === null || f.diagnosis === undefined ? '' : f.diagnosis).trim();
  if (dx) patch.diagnosis = dx;
  if (f.cancer_stage) patch.cancer_stage = f.cancer_stage;
  if (f.delivery_method) patch.delivery_method = f.delivery_method;
  return patch;
}

/**
 * Save the clinical profile fields the reports band on: diagnosis, cancer stage
 * and delivery method. These columns existed on `cases` from the start but had
 * no capture surface anywhere in either app, so every tile that reads them was
 * honestly reporting "not recorded" for every patient.
 *
 * Only the fields actually filled in are written (see clinicalPatch) and the
 * audit line names them, so the trail says WHAT changed rather than just that
 * the record was touched.
 * @param {string} caseId
 * @param {{diagnosis?: string, cancer_stage?: string, delivery_method?: string}} form
 * @param {{client?: any}} [deps]
 * @returns {Promise<any>}
 */
export async function updateClinicalDetails(caseId, form, deps) {
  const client = (deps && deps.client) || getClient();
  const patch = clinicalPatch(form);
  if (!Object.keys(patch).length) return fail(new Error('Nothing to save'));
  patch.updated_at = new Date().toISOString();
  const { error } = await client.from(TABLES.CASES).update(patch).eq('case_id', caseId);
  if (error) return fail(error);
  const named = Object.keys(patch)
    .filter((k) => k !== 'updated_at')
    .map((k) => k.replace(/_/g, ' '))
    .join(', ');
  await logAudit(AUDIT_EVENT.DATA_CHANGED, `updated clinical details — ${named}`, caseId, patch, deps);
  return ok(patch);
}

export async function logContact(caseId, entry, deps) {
  const client = (deps && deps.client) || getClient();
  const session = (deps && deps.session) || store.get(SLICES.SESSION) || {};
  const uid = session.user && session.user.id;
  const outcome = entry && entry.outcome;
  // Preserve "which party was reached" as a first-class column (legacy
  // execSaveCall wrote reached_party). The port folds the party into the outcome
  // value, so derive it back for the stored row — also de-risks a NOT NULL
  // reached_party in the base schema. (legacy v20 L5321.)
  const out = String(outcome || '');
  const reachedParty = out.includes('patient') ? 'patient' : out.includes('caregiver') ? 'caregiver' : 'unknown';
  const row = {
    case_id: caseId,
    channel: (entry && entry.channel) || 'call',
    outcome,
    reached_party: reachedParty,
    notes: (entry && entry.note) || null,
    created_by: uid || null,
    contact_at: new Date().toISOString(),
  };
  const { error } = await client.from(TABLES.CONTACTS).insert([row]);
  if (error) return fail(error);
  await logAudit(
    AUDIT_EVENT.CONTACT_LOGGED,
    `logged a contact — ${row.outcome} (via ${row.channel})`,
    caseId,
    { outcome: row.outcome, channel: row.channel },
    deps
  );
  return ok(row);
}
